import { useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';

interface FetchOptions extends RequestInit {
  /** Request key for deduplication - requests with same key cancel previous ones */
  requestKey?: string;
}

interface UseAuthFetchReturn {
  /** Authenticated fetch with automatic token handling and cancellation support */
  authFetch: (url: string, options?: FetchOptions) => Promise<Response>;
  /** Cancel all pending requests from this hook instance */
  cancelPendingRequests: () => void;
  /** Check if response was cancelled */
  isCancelled: (response: Response) => boolean;
}

/**
 * Hook for making authenticated API requests with automatic cancellation support.
 *
 * Features:
 * - Automatic token refresh on 401
 * - Request cancellation on unmount
 * - Request deduplication by key (same key cancels previous request)
 * - AbortError handling (cancelled requests don't trigger errors)
 *
 * @example
 * const { authFetch, cancelPendingRequests, isCancelled } = useAuthFetch();
 *
 * const fetchData = async () => {
 *   const response = await authFetch('/api/data', { requestKey: 'my-data' });
 *   if (isCancelled(response)) return; // User navigated away
 *   const data = await response.json();
 * };
 */
export function useAuthFetch(): UseAuthFetchReturn {
  const { authFetch: contextAuthFetch } = useAuth();
  const { activeTenant } = useAdminContext();
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Cancel all requests on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();
    };
  }, []);

  const authFetch = useCallback(async (
    url: string,
    options: FetchOptions = {}
  ): Promise<Response> => {
    const { requestKey, ...fetchOptions } = options;
    const key = requestKey || url;

    // Cancel any previous request with the same key
    const existingController = abortControllersRef.current.get(key);
    if (existingController) {
      console.log(`[useAuthFetch] Cancelling previous request: ${key}`);
      existingController.abort();
    }

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllersRef.current.set(key, controller);

    try {
      // Inject X-Tenant-Id header when super admin is in tenant context
      const headers = new Headers(fetchOptions.headers || {});
      if (activeTenant?.id) {
        headers.set('X-Tenant-Id', activeTenant.id);
      }

      const response = await contextAuthFetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      return response;
    } catch (error) {
      // AbortError means the request was cancelled - return a "cancelled" response
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`[useAuthFetch] Request cancelled: ${url}`);
        // Create a cancelled response marker (using 499 as "Client Closed Request")
        // We use a custom object since Response constructor doesn't allow status 0 in jsdom
        return {
          status: 0,
          ok: false,
          statusText: 'Cancelled',
          headers: new Headers({ 'Content-Type': 'application/json' }),
          json: async () => ({ error: 'Request cancelled' }),
          text: async () => JSON.stringify({ error: 'Request cancelled' }),
          body: null,
          bodyUsed: false,
          clone: () => ({ status: 0 } as Response),
          arrayBuffer: async () => new ArrayBuffer(0),
          blob: async () => new Blob(),
          formData: async () => new FormData(),
          redirected: false,
          type: 'basic',
          url: url,
        } as Response;
      }
      throw error;
    } finally {
      // Clean up controller if it's still the active one
      if (abortControllersRef.current.get(key) === controller) {
        abortControllersRef.current.delete(key);
      }
    }
  }, [contextAuthFetch, activeTenant]);

  const cancelPendingRequests = useCallback(() => {
    console.log(`[useAuthFetch] Cancelling ${abortControllersRef.current.size} pending requests`);
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
  }, []);

  const isCancelled = useCallback((response: Response): boolean => {
    return response.status === 0;
  }, []);

  return { authFetch, cancelPendingRequests, isCancelled };
}

export default useAuthFetch;
